import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { userService } from '../../services/user';
import { consumoService } from '../../services/consumo';
import { licenciasService } from '../../services/licencias';
import Loading from '../common/Loading';
import StatisticsCharts from '../charts/StatisticsCharts';
import {
    User,
    Users,
    Activity,
    CheckCircle,
    Calendar,
    Settings,
    BarChart3,
    Shield,
    Package,
    ClipboardList,
    FileText,
    RefreshCw,
    TrendingUp,
    AlertTriangle,
    Zap,
    Building2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
        blue: 'bg-blue-500',
        green: 'bg-green-500',
        purple: 'bg-purple-500',
        orange: 'bg-orange-500'
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
                <div className={`${colorClasses[color]} p-3 rounded-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
            </div>
        </div>
    );
};

// Etiqueta/color del rol para la tarjeta de Perfil de Usuario (mismo set de
// roles que pages/UsersPage.jsx).
const ROLE_LABELS = {
    admin: 'Administrador',
    master: 'Master',
    lector: 'Lector',
    xd: 'XD',
    sst: 'SST',
    validador: 'Validador',
    limpieza: 'Limpieza',
    rrhh: 'RRHH',
    chile: 'Chile',
    user: 'Usuario',
};

const getRoleLabel = (role) => ROLE_LABELS[role] || 'Usuario';

const Dashboard = () => {
    const { user, isAdmin } = useAuth();
    const { data: stats, loading, error, refetch: refetchStats } = useApi(() => userService.getDashboardStats());
    const [recentActivity, setRecentActivity] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(false);
    const [statsKWH, setStatsKWH] = useState(null);
    const [licenciasVencidas, setLicenciasVencidas] = useState([]);

    useEffect(() => {
        if (isAdmin()) {
            consumoService.statsKWH()
                .then(setStatsKWH)
                .catch(() => {});
            licenciasService.resumen()
                .then((items) => setLicenciasVencidas(items.filter((it) => it.estado === 'vencido')))
                .catch(() => {});
        }
    }, []);

    // Función para obtener actividad reciente
    const fetchRecentActivity = async () => {
        setLoadingActivity(true);
        try {
            const data = await userService.getRecentActivity();
            setRecentActivity(data);
        } catch (error) {
            console.error('Error fetching recent activity:', error);
        } finally {
            setLoadingActivity(false);
        }
    };

    // Función de refresh completo
    const refreshAllData = () => {
        refetchStats();
        fetchRecentActivity();
    };

    // Hook de auto-refresh
    const { isEnabled: autoRefresh, setIsEnabled: setAutoRefresh, manualRefresh } = useAutoRefresh(
        refreshAllData,
        30000
    );

    // Cargar actividad reciente al montar
    useEffect(() => {
        fetchRecentActivity();
    }, []);

    // Función manual de refresh que actualiza el hook
    const handleManualRefresh = () => {
        manualRefresh();
    };

    if (loading) return <Loading message="Cargando dashboard..." />;
    if (error) return <div className="text-red-600">Error: {error}</div>;

    // Enlaces rápidos basados en el rol
    const getQuickActions = () => {
        const commonActions = [
            {
                title: 'Ver Solicitudes',
                description: 'Gestiona las solicitudes de trabajo',
                icon: FileText,
                link: '/job-application',
                color: 'text-blue-600 hover:text-blue-800'
            }
        ];

        const adminActions = [
            {
                title: 'Gestionar Activos',
                description: 'Administra los activos de la empresa',
                icon: Package,
                link: '/activos',
                color: 'text-green-600 hover:text-green-800'
            },
            {
                title: 'Órdenes de Trabajo',
                description: 'Crea y gestiona órdenes de trabajo',
                icon: ClipboardList,
                link: '/work-orders',
                color: 'text-purple-600 hover:text-purple-800'
            },
            {
                title: 'Gestionar Usuarios',
                description: 'Administra los usuarios del sistema',
                icon: Users,
                link: '/users',
                color: 'text-orange-600 hover:text-orange-800'
            }
        ];

        return isAdmin() ? [...commonActions, ...adminActions] : commonActions;
    };

    const quickActions = getQuickActions();

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                        <h1 className="text-3xl font-bold text-gray-900">
                            ¡Bienvenido, {user?.full_name}!
                        </h1>
                        {isAdmin() && (
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                <Shield className="h-4 w-4 inline mr-1" />
                                Administrador
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleManualRefresh}
                            className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Actualizar
                        </button>
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                                autoRefresh 
                                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            }`}
                        >
                            <TrendingUp className="h-4 w-4 mr-1" />
                            Auto: {autoRefresh ? 'ON' : 'OFF'}
                        </button>
                    </div>
                </div>
                <p className="text-gray-600">
                    Dashboard dinámico - Datos actualizados automáticamente cada 30 segundos
                </p>
            </div>

            {/* Stats Grid - Diferentes tarjetas según rol */}
            <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin() ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-6 mb-8`}>
                {/* Solo admin ve Total Usuarios */}
                {isAdmin() && (
                    <StatCard
                        title="Total Usuarios"
                        value={stats?.total_users || 0}
                        icon={Users}
                        color="blue"
                    />
                )}

                {/* Todos ven Solicitudes Pendientes */}
                <StatCard
                    title={isAdmin() ? "Solicitudes Pendientes" : "Mis Solicitudes"}
                    value={isAdmin() ? (stats?.solicitudes?.pendientes || 0) : (stats?.solicitudes?.mis_solicitudes || 0)}
                    icon={FileText}
                    color="orange"
                />

                {/* Solo admin ve Órdenes en Proceso */}
                {isAdmin() && (
                    <StatCard
                        title="Órdenes en Proceso"
                        value={stats?.ordenes_trabajo?.en_proceso || 0}
                        icon={ClipboardList}
                        color="purple"
                    />
                )}

                {/* Solo admin ve Activos en Servicio */}
                {isAdmin() && (
                    <StatCard
                        title="Activos en Servicio"
                        value={stats?.activos?.en_servicio || 0}
                        icon={Package}
                        color="green"
                    />
                )}

                {/* Usuario normal ve sus solicitudes finalizadas */}
                {!isAdmin() && (
                    <StatCard
                        title="Solicitudes Finalizadas"
                        value={stats?.solicitudes?.mis_finalizadas || 0}
                        icon={CheckCircle}
                        color="green"
                    />
                )}
            </div>

            {/* Alerta de documentos vencidos (Licencias/ITSE) - Solo para admin */}
            {isAdmin() && licenciasVencidas.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-8">
                    <div className="flex items-center mb-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                        <h3 className="text-lg font-semibold text-red-800">
                            {licenciasVencidas.length} documento{licenciasVencidas.length !== 1 ? 's' : ''} vencido{licenciasVencidas.length !== 1 ? 's' : ''} (Licencias e ITSE)
                        </h3>
                    </div>
                    <div className="space-y-1 mb-3">
                        {licenciasVencidas.slice(0, 5).map((it) => (
                            <p key={`${it.almacen}-${it.tipo_documento}`} className="text-sm text-red-700">
                                <span className="font-semibold">{it.almacen}</span> — {it.tipo_documento_nombre}
                            </p>
                        ))}
                        {licenciasVencidas.length > 5 && (
                            <p className="text-sm text-red-600">y {licenciasVencidas.length - 5} más...</p>
                        )}
                    </div>
                    <Link
                        to="/gestion-documental/licencias-itse/resumen"
                        className="inline-block text-sm font-semibold text-red-700 hover:underline"
                    >
                        Ver todos los vencidos →
                    </Link>
                </div>
            )}

            {/* Gráficos Estadísticos - Solo para admin */}
            {isAdmin() && <StatisticsCharts stats={stats} />}

            {/* Detailed Stats - Solo para Admins */}
            {isAdmin() && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <Users className="h-5 w-5 text-blue-600 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-900">Usuarios</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total:</span>
                                <span className="font-medium">{stats?.total_users || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Activos:</span>
                                <span className="font-medium text-green-600">{stats?.active_users || 0}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <FileText className="h-5 w-5 text-orange-600 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-900">Solicitudes</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total:</span>
                                <span className="font-medium">{stats?.solicitudes?.total || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Pendientes:</span>
                                <span className="font-medium text-orange-600">{stats?.solicitudes?.pendientes || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Últimos 7 días:</span>
                                <span className="font-medium text-blue-600">{stats?.solicitudes?.recientes_7_dias || 0}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <Package className="h-5 w-5 text-green-600 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-900">Activos</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total:</span>
                                <span className="font-medium">{stats?.activos?.total || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">En servicio:</span>
                                <span className="font-medium text-green-600">{stats?.activos?.en_servicio || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Fuera de servicio:</span>
                                <span className="font-medium text-red-600">{stats?.activos?.fuera_servicio || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Estadísticas KWH por Almacén - Solo admin */}
            {isAdmin() && statsKWH && statsKWH.por_almacen?.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                            <Zap className="h-5 w-5 text-yellow-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-900">Consumo Eléctrico — Almacenes</h3>
                        </div>
                        <div className="flex items-center space-x-4 text-sm">
                            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-medium">
                                {statsKWH.total_kwh?.toLocaleString('es-PE', { maximumFractionDigits: 1 })} kWh total
                            </span>
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                                S/. {statsKWH.total_soles?.toFixed(2)} total
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {statsKWH.por_almacen.map((a) => {
                            const pct = statsKWH.total_kwh > 0 ? (a.kwh / statsKWH.total_kwh) * 100 : 0;
                            return (
                                <div key={a.almacen} className="border border-gray-200 rounded-xl p-4">
                                    <div className="flex items-center space-x-1 mb-2">
                                        <Building2 className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="text-xs font-semibold text-gray-700 truncate">{a.almacen}</span>
                                    </div>
                                    <p className="text-lg font-bold text-yellow-600">
                                        {a.kwh.toLocaleString('es-PE', { maximumFractionDigits: 1 })}
                                        <span className="text-xs font-normal text-gray-400 ml-1">kWh</span>
                                    </p>
                                    <p className="text-sm font-semibold text-green-700">S/. {a.soles.toFixed(2)}</p>
                                    <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5">
                                        <div
                                            className="bg-yellow-400 h-1.5 rounded-full transition-all"
                                            style={{ width: `${Math.min(pct, 100)}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1 text-right">{pct.toFixed(1)}%</p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 text-right">
                        <Link to="/consumo-servicio" className="text-xs text-blue-600 hover:underline">
                            Ver detalle →
                        </Link>
                    </div>
                </div>
            )}

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Profile Card */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                        <User className="h-5 w-5 text-blue-600 mr-2" />
                        <h2 className="text-xl font-semibold text-gray-900">Perfil de Usuario</h2>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Nombre Completo</label>
                            <p className="text-gray-900">{user?.full_name}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Email</label>
                            <p className="text-gray-900">{user?.email}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Rol</label>
                            <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    user?.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {user?.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                                    {getRoleLabel(user?.role)}
                                </span>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Estado</label>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Activo
                            </span>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Miembro desde</label>
                            <p className="text-gray-900">
                                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                        <Settings className="h-5 w-5 text-blue-600 mr-2" />
                        <h2 className="text-xl font-semibold text-gray-900">Acciones Rápidas</h2>
                    </div>
                    <div className="space-y-3">
                        {quickActions.map((action, index) => (
                            <Link
                                key={index}
                                to={action.link}
                                className="block p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                            >
                                <div className="flex items-center">
                                    <action.icon className={`h-5 w-5 mr-3 ${action.color}`} />
                                    <div>
                                        <p className="font-medium text-gray-900">{action.title}</p>
                                        <p className="text-sm text-gray-600">{action.description}</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="mt-8 bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <Activity className="h-5 w-5 text-blue-600 mr-2" />
                        <h2 className="text-xl font-semibold text-gray-900">Actividad Reciente</h2>
                    </div>
                    {loadingActivity && (
                        <div className="flex items-center text-sm text-gray-500">
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            Cargando...
                        </div>
                    )}
                </div>
                <div className="space-y-3">
                    {/* Actividad dinámica del backend */}
                    {recentActivity.length > 0 ? (
                        recentActivity.map((activity, index) => (
                            <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <div className={`w-2 h-2 rounded-full mr-3 ${
                                    activity.color === 'blue' ? 'bg-blue-500' :
                                    activity.color === 'green' ? 'bg-green-500' :
                                    activity.color === 'orange' ? 'bg-orange-500' :
                                    'bg-purple-500'
                                }`}></div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                                    <p className="text-xs text-gray-600">{activity.description}</p>
                                    <div className="flex items-center mt-1 space-x-2">
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            activity.status === 'PENDIENTE' ? 'bg-orange-100 text-orange-800' :
                                            activity.status === 'EN_PROCESO' ? 'bg-blue-100 text-blue-800' :
                                            'bg-green-100 text-green-800'
                                        }`}>
                                            {activity.status}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {new Date(activity.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <>
                            {/* Actividad estática inicial */}
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Inicio de sesión exitoso</p>
                                    <p className="text-xs text-gray-600">Hace unos momentos</p>
                                </div>
                            </div>
                            {isAdmin() && (
                                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Permisos de administrador activos</p>
                                        <p className="text-xs text-gray-600">Acceso completo al sistema</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Cuenta creada</p>
                                    <p className="text-xs text-gray-600">
                                        {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Fecha no disponible'}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                    
                    {/* Mis solicitudes personales */}
                    {stats?.solicitudes?.mis_solicitudes > 0 && (
                        <div className="flex items-center p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                            <FileText className="h-4 w-4 text-blue-600 mr-3" />
                            <div>
                                <p className="text-sm font-medium text-gray-900">Mis Solicitudes</p>
                                <p className="text-xs text-blue-600">
                                    Tienes {stats.solicitudes.mis_solicitudes} solicitud(es) activa(s)
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;